import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useEffect, useState } from "react";
import { TiPencil } from "react-icons/ti";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaTags } from "react-icons/fa";
import { Pagination } from "../../components/common/pagination/Pagination";
import { useQueryParams } from "../../hooks/useQueryParams";
import { toast } from "react-toastify";
import { deleteBrand, getBrands } from "../../redux/slices/brandSlice";
import { getErrorMessage } from "../../utils/errorHandler";
import { ConfirmModal } from "../common/ConfirmModal";
import { PageHeader } from "../common/PageHeader";
import { SearchInput } from "../common/SearchInput";
import { EmptyState } from "../common/EmptyState";
import { StatusBadge } from "../common/StatusBadge";
import { DataTable } from "../common/DataTable";

type Payload = {
  onCreate: () => void;
  onUpdate: (id: string) => void;
};

export const ListBrand = ({ onCreate, onUpdate }: Payload) => {
  const dispatch = useAppDispatch();
  const { brands, pagination, loading } = useAppSelector(
    (state) => state.brand,
  );
  const { getParam, updateParams } = useQueryParams();

  const page = Number(getParam("page", 1));
  const limit = Number(getParam("limit", 10));
  const isActive = getParam("isActive", "all");
  const search = getParam("search", "");

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateParams({ [name]: value, page: 1 });
  };

  const parserIsActive =
    isActive === "all" ? undefined : isActive === "true" ? true : false;
  useEffect(() => {
    dispatch(getBrands({ search, page, limit, isActive: parserIsActive }));
  }, [dispatch, search, page, limit, parserIsActive]);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await dispatch(deleteBrand(confirmDeleteId)).unwrap();
      toast.success("Xóa thương hiệu thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="p-5">
      <PageHeader
        title="Quản lý thương hiệu"
        subtitle={
          pagination ? `Tổng ${pagination.total} thương hiệu` : undefined
        }
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer text-sm text-white rounded-lg font-medium"
          >
            + Thêm thương hiệu
          </button>
        }
      />

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 border-b border-gray-100">
          <SearchInput
            value={search}
            onChange={(v) => updateParams({ search: v, page: 1 })}
            placeholder="Tìm thương hiệu theo tên..."
            className="w-full lg:w-80"
          />
          <div className="flex items-center gap-2">
            <select
              name="isActive"
              value={isActive}
              onChange={handleChange}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="true">Hoạt động</option>
              <option value="false">Ngừng hoạt động</option>
            </select>
            <select
              name="limit"
              value={limit}
              onChange={handleChange}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="10">10 / trang</option>
              <option value="20">20 / trang</option>
              <option value="30">30 / trang</option>
              <option value="50">50 / trang</option>
            </select>
          </div>
        </div>

        <DataTable
          data={brands}
          rowKey={(b) => b._id}
          loading={loading.list}
          columns={[
            {
              key: "logo",
              header: "Logo",
              align: "center",
              width: "80px",
              render: (b) => (
                <img
                  src={b.logo?.url}
                  alt={b.name}
                  className="h-12 w-12 border border-gray-200 rounded-lg object-cover mx-auto"
                />
              ),
            },
            {
              key: "name",
              header: "Tên thương hiệu",
              align: "left",
              render: (b) => (
                <span className="font-medium text-gray-900">{b.name}</span>
              ),
            },
            {
              key: "status",
              header: "Trạng thái",
              align: "center",
              render: (b) =>
                b.isActive ? (
                  <StatusBadge tone="success" label="Hoạt động" />
                ) : (
                  <StatusBadge tone="danger" label="Ngừng" />
                ),
            },
            {
              key: "actions",
              header: "Thao tác",
              align: "right",
              width: "100px",
              render: (b) => (
                <div className="flex items-center gap-1 justify-end">
                  <button
                    type="button"
                    title="Sửa"
                    onClick={() => onUpdate(b._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <TiPencil className="text-lg" />
                  </button>
                  <button
                    type="button"
                    title="Xóa"
                    onClick={() => setConfirmDeleteId(b._id)}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <RiDeleteBinLine className="text-lg" />
                  </button>
                </div>
              ),
            },
          ]}
          empty={
            <EmptyState
              icon={<FaTags />}
              title={
                search ? "Không tìm thấy thương hiệu nào" : "Chưa có thương hiệu"
              }
              description={
                search ? "Thử tìm với từ khóa khác" : "Thêm thương hiệu đầu tiên"
              }
              action={
                !search ? (
                  <button
                    type="button"
                    onClick={onCreate}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white cursor-pointer"
                  >
                    + Thêm thương hiệu
                  </button>
                ) : undefined
              }
            />
          }
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center mt-4">
          <Pagination
            pagination={pagination}
            onChange={(newPage) => updateParams({ page: newPage })}
          />
        </div>
      )}

      {confirmDeleteId && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn xóa thương hiệu này? Hành động này không thể hoàn tác."
          onConfirm={handleDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
};
